import React, { useEffect, useState } from 'react';
import { Maximize2, Minimize2, Minus, X } from 'lucide-react';

interface WindowControlsProps {
  onMinimize?: () => void;
  onMaximize?: () => void;
  onClose?: () => void;
}

export default function WindowControls({
  onMinimize,
  onMaximize,
  onClose,
}: WindowControlsProps) {
  const [isMaximized, setIsMaximized] = useState(false);

  useEffect(() => {
    if (!window.windowAPI && !window.electron) return;

    const handleResize = () => {
      const isMax =
        window.innerWidth >= screen.width * 0.9 &&
        window.innerHeight >= screen.availHeight * 0.9;
      setIsMaximized(isMax);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const windowControlsApi = window.windowAPI ?? window.electron;
  const isDesktopShell = Boolean(windowControlsApi);

  const handleMinimize = () => {
    onMinimize?.();
    if (!isDesktopShell) return;
    windowControlsApi?.minimize?.();
  };

  const handleMaximize = () => {
    onMaximize?.();
    if (!isDesktopShell) return;
    windowControlsApi?.maximize?.();
  };

  const handleClose = () => {
    onClose?.();
    if (!isDesktopShell) return;
    windowControlsApi?.close?.();
  };

  const baseButtonClass =
    'group flex h-8 w-8 items-center justify-center rounded-sm transition-colors duration-150';
  const iconClass =
    'text-slate-600 transition-colors duration-150 group-hover:text-slate-900 dark:text-slate-400 dark:group-hover:text-slate-100';

  return (
    <div className={`window-controls flex items-center gap-0 ${isDesktopShell ? '' : 'opacity-70'}`}>
      <button
        type="button"
        onClick={handleMinimize}
        className={`${baseButtonClass} hover:bg-slate-200/70 dark:hover:bg-slate-800/72`}
        aria-label="Minimizar"
        title="Minimizar"
        disabled={!isDesktopShell}
      >
        <Minus size={13} strokeWidth={1.5} className={iconClass} />
      </button>
      <button
        type="button"
        onClick={handleMaximize}
        className={`${baseButtonClass} hover:bg-slate-200/70 dark:hover:bg-slate-800/72`}
        aria-label={isMaximized ? 'Restaurar' : 'Maximizar'}
        title={isMaximized ? 'Restaurar' : 'Maximizar'}
        disabled={!isDesktopShell}
      >
        {isMaximized ? (
          <Minimize2 size={12} strokeWidth={1.5} className={iconClass} />
        ) : (
          <Maximize2 size={12} strokeWidth={1.5} className={iconClass} />
        )}
      </button>
      <button
        type="button"
        onClick={handleClose}
        className={`${baseButtonClass} hover:bg-red-500/14 dark:hover:bg-red-500/18`}
        aria-label="Fechar"
        title="Fechar"
        disabled={!isDesktopShell}
      >
        <X
          size={13}
          strokeWidth={1.5}
          className="text-slate-600 transition-colors duration-150 group-hover:text-red-600 dark:text-slate-400 dark:group-hover:text-red-400"
        />
      </button>
    </div>
  );
}

declare global {
  interface Window {
    windowAPI?: {
      minimize?: () => void;
      maximize?: () => void;
      close?: () => void;
      setBackgroundColor?: (color: string) => void;
    };
    electron?: {
      minimize?: () => void;
      maximize?: () => void;
      close?: () => void;
      setBackgroundColor?: (color: string) => void;
    };
  }
}
